/* eslint-disable no-debugger */
import { useState, useEffect } from 'react';
import useForm from '@usas/ui-web/utils/UseForm';
import validate from "./RelayTimesDetailKendoReportValidate";
import useReportPeriodFlatData from '@usas/ui-web/state/reportPeriodFlat/UseReportPeriodFlatData';
import useTimeStandardTypeData from '@usas/ui-web/state/timeStandardType/UseTimeStandardTypeData';
import Constants from '@usas/ui-web/utils/Constants';
import ToIntIfInt from '@usas/ui-web/utils/ToIntIfInt';

import useSecurityData from '@usas/ui-web/state/security/UseSecurityData';
import useOrgUnitData from '@usas/ui-web/state/orgUnitForPromise/UseOrgUnitData';

// Define the columns configuration for this report
const columns = [
  { width: "90px", id: "Rank", field: "rank", title: "Rank" },
  { width: "100px", id: "EventGender", field: "eventgender", title: "Competition Category" },
  { width: "100px", id: "EventCode", field: "eventcode", title: "Event" },
  { width: "110px", id: "SwimTime", field: "swimtime", title: "Swim Time" },
  { width: "380px", id: "RelayMemberNames", field: "relaymembernames", title: "Member Names" },
  { width: "90px", id: "LscCode", field: "lsccode", title: "LSC" },    
  { width: "110px", id: "ClubCode", field: "clubcode", title: "Club Code" },
  { width: "175px", id: "ClubName", field: "clubname", title: "Club Name" },
  { width: "90px", id: "AgeGroup", field: "agegroup", title: "Age Group" },
  { width: "375px", id: "MeetName", field: "meetname", title: "Meet" },
  { width: "110px", id: "SwimDate", field: "swimdate", title: "Date", format: "{0:MM/dd/yyyy}" }
];

const excelColumns = [
  { id: "Rank", field: "rank", title: "Rank" },
  { id: "EventGender", field: "eventgender", title: "Competition Category" },
  { id: "EventCode", field: "eventcode", title: "Event" },
  { id: "SwimTime", field: "swimtime", title: "Swim Time" },
  { id: "RelayMemberNames", field: "relaymembernames", title: "Member Names" },
  { id: "LscCode", field: "lsccode", title: "LSC" },
  { id: "ClubCode", field: "clubcode", title: "Club Code" },
  { id: "ClubName", field: "clubname", title: "Club Name" },
  { id: "AgeGroup", field: "agegroup", title: "Age Group" },
  { id: "MeetName", field: "meetname", title: "Meet" },
  { id: "SwimDate", field: "swimdate", title: "Date", format: "{0:MM/dd/yyyy}" },
  { id: "Splits_Age", field: "splits_age", title: "Age" },
  { id: "Splits_LegNumber", field: "splits_legNumber", title: "Leg Number" },
  { id: "Splits_LegTime", field: "splits_legTime", title: "Leg Time" },
  { id: "Splits_Name", field: "splits_name", title: "Name" }
];

const useRelayTimesDetailKendoReport = () => {
  const { reportPeriodFlatState, getReportPeriodsFlatByOrganizationId } = useReportPeriodFlatData();
  const [seasonYearState, setSeasonYearState] = useState({ isLoading: true, isLoaded: false, options: [] });
  const { timeStandardTypeState, getTimeStandardTypesByOrgUnitId } = useTimeStandardTypeData();
  const [filteredTimeStandardTypeState, setFilteredTimeStandardTypeState] = useState({ isLoading: true, isLoaded: false, options: [] });
  const [modalState, setModalState] = useState(getInitialModalState());

  const { securityState } = useSecurityData();
  const { orgUnitState, getOrgUnitByOrgUnitId } = useOrgUnitData();

  const {
    formState,
    errorState,
    handleSubmit,
    updateFormState,
    onFormValueChanged,
    onValueTextPairChanged,
    setFormState,
    setIsDirty
  } = useForm(getInitialFormState(), submitFormCallback, validate);

  function submitFormCallback() {
    setModalState({ ...modalState, displayPopUp: true });
  }

  const onRunReportClicked = (e) => {
    handleSubmit(e);
  }

  const onClearForm = () => {
    setFormState(getInitialFormState());
    setIsDirty(true);
  }

  const onModalCancelClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setIsDirty(true);
    setModalState({ ...modalState, displayPopUp: false });
  };

  function getInitialModalState() {
    return {
      displayPopUp: false,
      currentPrimaryInfo: '',
      modalTitle: 'Report Viewer'
    };
  }

  function getInitialFormState() {
    return {
      courses: [],
      events: [],
      startDate: Constants.BLANK_DATE_STRING,
      endDate: Constants.BLANK_DATE_STRING,
      seasonId: -1,
      seasonYear: -1,
      minAge: 1,
      maxAge: 99,
      genders: [{ id: 1, name: 'Female' }, { id: 2, name: 'Male' }],
      excludeNcaa: false,
      showOlyEvent: false,
      clubs: [],
      lscs: [],
      zones: [],
      topTimes: 10,
      exportDetailData: false
    };
  }

  useEffect(() => {
    if (reportPeriodFlatState.isLoading === false && reportPeriodFlatState.isLoaded === false) {
      getReportPeriodsFlatByOrganizationId(1);
    }

    if (timeStandardTypeState.isArrayLoading === false && timeStandardTypeState.isArrayLoaded === false) {
      getTimeStandardTypesByOrgUnitId(3);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (securityState.userInfo?.memberId.length > 0) {
      setFormState({ ...formState, orgUnitId: securityState.userInfo?.primaryOrgUnitId });

      const getOrgUnitByOrgUnitIdPromise = getOrgUnitByOrgUnitId(securityState.userInfo?.primaryOrgUnitId);

      if (getOrgUnitByOrgUnitIdPromise !== null) {
        getOrgUnitByOrgUnitIdPromise.then((newState) => {
          if (newState !== null) {
            // nothing to do here
          }
        }).catch((e) => {
          //TODO state error
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [securityState]);

  useEffect(() => {
    if (reportPeriodFlatState.isLoaded === true) {
      const years = [];

      reportPeriodFlatState.dataAsOptionsWithNames.map(rp => {
        if (rp.name.substring(5, 6) === '(') {
          years.unshift({ id: rp.id, name: rp.name.substring(0, 4) });
        }
      });
      years.unshift({ id: -1, name: '--' });
      setSeasonYearState({ isLoading: false, isLoaded: true, options: years });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportPeriodFlatState]);

  useEffect(() => {
    if (timeStandardTypeState.isArrayLoaded === true) {
      const standardTypes = [];
      timeStandardTypeState.arrayData.map(st => {
        if (st.assignToTimes === true && st.availableToHq === true) {
          standardTypes.push({ id: st.sortOrder, name: st.typeName });
        }
      });
      setFilteredTimeStandardTypeState({ isLoading: false, isLoaded: true, options: standardTypes });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeStandardTypeState]);

  return {
    formState,
    errorState,
    reportParameters: {
      Courses: formState.courses.map(course => course.id).join(',') || null, 
      Events: formState.events.map(event => event.id).join(',') || null, 
      StartDate: formState.startDate === Constants.BLANK_DATE_STRING ? null : formState.startDate, 
      EndDate: formState.endDate === Constants.BLANK_DATE_STRING ? null : formState.endDate, 
      SeasonYear_Int: formState.seasonYear === Constants.DEFAULT_ID ? null : parseInt(formState.seasonYear, 10), 
      MinAge_Int: ToIntIfInt(formState.minAge), 
      MaxAge_Int: ToIntIfInt(formState.maxAge), 
      Genders: formState.genders.map(gender => gender.id).join(','), 
      ExcludeNcaa_Int: formState.excludeNcaa === true ? 1 : 0, 
      Clubs: formState.clubs.map(club => club.id).join(',') || null, 
      LSCs: formState.lscs.map(lsc => lsc.id).join(',') || null, 
      Zones: formState.zones.map(zone => zone.id).join(',').length === 0 ? null : formState.zones.map(zone => zone.id).join(','), 
      TopTime_Int: ToIntIfInt(formState.topTimes)
    },
    parameters: formState,
    columns: columns,
    modalState,
    orgUnitState,
    onModalCancelClicked,
    handleSubmit,
    onFormValueChanged,
    onValueTextPairChanged,
    updateFormState,
    onRunReportClicked,
    onClearForm,
    seasonYearState,
    filteredTimeStandardTypeState,
    excelColumns
  };
};

export default useRelayTimesDetailKendoReport;